import React, { useState, useEffect, createContext, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { showUpgradeRequired } from './UpgradeRequiredDialog';
import LanguageSwitcher from './LanguageSwitcher';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';

// Prevents double-rendering when pages wrap themselves in Layout AND
// App.js uses a persistent AppShell. Inner Layout calls become no-ops.
const LayoutMountedContext = createContext(false);
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  House,
  Student,
  Users,
  Buildings,
  CheckCircle,
  BookOpen,
  BookBookmark,
  CurrencyCircleDollar,
  CalendarBlank,
  Megaphone,
  Gear,
  SignOut,
  List,
  UserCircle,
  Bus,
  DoorOpen,
  Wallet,
  Receipt,
  Truck,
  Bed,
  Phone,
  CreditCard,
  Ticket,
  ShieldCheck,
  MapPin,
  QrCode,
  NavigationArrow,
  SignIn,
  ChalkboardTeacher,
  UserCheck,
  UserPlus,
  FirstAidKit,
  Syringe,
  Warning,
  Ambulance,
  MagnifyingGlass,
  Package,
  Archive,
  GraduationCap,
  Certificate,
  IdentificationCard,
  MapTrifold,
  ClipboardText,
  Wrench,
  FileText,
  ChartBar,
  Plugs,
  Lock,
  CaretDown,
  Clock,
  Question,
} from '@phosphor-icons/react';

// Sidebar Navigation
// Maps the small set of top-level nav labels this round translates (see
// src/locales/*/common.json's "nav" keys) back to their i18n key — everything
// else (the ~80 leaf items) falls through to the raw English label, which
// is intentional scope (framework + core surfaces, not every string).
const NAV_LABEL_KEYS = {
  'Dashboard': 'nav.dashboard',
  'Academics': 'nav.academics',
  'People': 'nav.people',
  'Attendance & Conduct': 'nav.attendanceConduct',
  'Finance': 'nav.finance',
  'Facilities & Logistics': 'nav.facilitiesLogistics',
  'Health': 'nav.health',
  'Inventory': 'nav.inventory',
  'Certificates & Credentials': 'nav.certificatesCredentials',
  'Communication': 'nav.communication',
  'Security': 'nav.security',
  'Programs': 'nav.programs',
  'Administration': 'nav.administration',
  'Reports': 'nav.reports',
};

function Sidebar({ isOpen, onClose }) {
  const { t } = useTranslation();
  const translateNavLabel = (label) => (NAV_LABEL_KEYS[label] ? t(NAV_LABEL_KEYS[label]) : label);
  const { user, logout, hasModule } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Add security items to admin/staff nav (also reused, flat, by super_admin's nav below)
  const adminSecurityItems = [
    { path: '/security', label: 'Security Hub', icon: ShieldCheck, roles: ['super_admin', 'school_admin'] },
    { path: '/security/admin-tracking', label: 'Security Tracker', icon: MapPin, roles: ['super_admin', 'school_admin'] },
    { path: '/security/gate-attendance', label: 'Gate Attendance', icon: SignIn, roles: ['super_admin', 'school_admin'], moduleKey: 'gate_attendance' },
  ];

  // Navigation items for staff (admin, teachers) — grouped into collapsible
  // domain sections; a small number of standalone items (Dashboard, Reports)
  // stay top-level since they don't belong to any domain.
  const staffNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: House, roles: ['super_admin', 'school_admin', 'teacher', 'student'] },
    {
      label: 'Academics', icon: BookOpen,
      roles: ['super_admin', 'school_admin', 'teacher', 'student'],
      children: [
        { path: '/classes', label: 'Classes', icon: Buildings, roles: ['super_admin', 'school_admin', 'teacher'] },
        { path: '/grades', label: 'Grades', icon: BookOpen, roles: ['super_admin', 'school_admin', 'teacher', 'student'] },
        { path: '/timetable', label: 'Timetable', icon: CalendarBlank, roles: ['super_admin', 'school_admin', 'teacher', 'student'], moduleKey: 'academic_reports' },
        { path: '/exams', label: 'Exam Scheduling', icon: ClipboardText, roles: ['super_admin', 'school_admin'] },
        { path: '/exam-office', label: 'Exam Office', icon: Certificate, roles: ['super_admin', 'school_admin', 'teacher', 'registrar', 'hr'] },
        { path: '/curriculum/planning', label: 'Curriculum Planning', icon: ClipboardText, roles: ['super_admin', 'school_admin', 'teacher'] },
        { path: '/tracks', label: 'Tracks', icon: GraduationCap, roles: ['super_admin', 'school_admin'] },
        { path: '/exam-board/registrations', label: 'Exam Board', icon: Certificate, roles: ['super_admin', 'school_admin'] },
        { path: '/library', label: 'E-Library', icon: BookBookmark, roles: ['super_admin', 'school_admin'] },
      ],
    },
    {
      label: 'People', icon: Users,
      roles: ['super_admin', 'school_admin', 'teacher', 'registrar'],
      children: [
        { path: '/students', label: 'Students', icon: Student, roles: ['super_admin', 'school_admin', 'teacher'] },
        { path: '/staff', label: 'Staff', icon: Users, roles: ['super_admin', 'school_admin'] },
        { path: '/hr/performance', label: 'Staff Performance', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr', 'teacher', 'security_officer', 'driver', 'nurse', 'registrar', 'storekeeper', 'canteen_staff'] },
        { path: '/hr/recruitment', label: 'Recruitment', icon: UserPlus, roles: ['super_admin', 'school_admin', 'hr'] },
        { path: '/hr/administration', label: 'HR Administration', icon: ClipboardText, roles: ['super_admin', 'school_admin', 'hr'] },
        { path: '/hr/overtime', label: 'Overtime', icon: Clock, roles: ['super_admin', 'school_admin', 'hr', 'teacher', 'security_officer', 'driver', 'nurse', 'registrar', 'storekeeper', 'canteen_staff'] },
        { path: '/admissions/applicants', label: 'Admissions', icon: UserPlus, roles: ['super_admin', 'school_admin', 'registrar'] },
        { path: '/alumni/records', label: 'Alumni', icon: GraduationCap, roles: ['super_admin', 'school_admin'] },
        { path: '/student-support', label: 'Student Support', icon: ShieldCheck, roles: ['super_admin', 'school_admin', 'hr', 'nurse', 'safeguarding_lead'] },
        { path: '/student-support/secure', label: 'Sensitive Support', icon: ShieldCheck, roles: ['super_admin', 'school_admin', 'hr', 'nurse', 'safeguarding_lead', 'counselor', 'sen_coordinator'] },
      ],
    },
    {
      label: 'Attendance & Conduct', icon: CheckCircle,
      roles: ['super_admin', 'school_admin', 'teacher', 'hr', 'registrar'],
      children: [
        { path: '/attendance', label: 'Attendance', icon: CheckCircle, roles: ['super_admin', 'school_admin', 'teacher'] },
        { path: '/staff-attendance', label: 'Staff Attendance', icon: UserCheck, roles: ['super_admin', 'school_admin', 'hr'], moduleKey: 'staff_attendance' },
        { path: '/leave-requests', label: 'Leave Requests', icon: CalendarBlank, roles: ['super_admin', 'school_admin', 'hr', 'teacher'] },
        { path: '/discipline/incidents', label: 'Discipline', icon: Warning, roles: ['super_admin', 'school_admin'] },
        { path: '/security/gate-attendance', label: 'Gate Attendance', icon: SignIn, roles: ['super_admin', 'school_admin', 'registrar'], moduleKey: 'gate_attendance' },
      ],
    },
    {
      label: 'Finance', icon: Receipt,
      roles: ['super_admin', 'school_admin', 'hr'],
      children: [
        { path: '/finance', label: 'Finance', icon: Receipt, roles: ['super_admin', 'school_admin', 'hr'] },
        { path: '/fees', label: 'Fees', icon: CurrencyCircleDollar, roles: ['super_admin', 'school_admin', 'parent'] },
        { path: '/billing', label: 'Billing', icon: CreditCard, roles: ['super_admin', 'school_admin'] },
        { path: '/admin/billing', label: 'Billing Admin', icon: CreditCard, roles: ['super_admin'] },
        { path: '/payroll', label: 'Payroll', icon: Wallet, roles: ['super_admin', 'school_admin', 'hr'], moduleKey: 'payroll' },
        { path: '/budget', label: 'Budget', icon: Receipt, roles: ['super_admin', 'school_admin', 'hr'], moduleKey: 'finance_advanced' },
      ],
    },
    {
      label: 'Facilities & Logistics', icon: Truck,
      roles: ['super_admin', 'school_admin', 'registrar'],
      children: [
        { path: '/operations', label: 'Facilities & Maintenance', icon: Wrench, roles: ['super_admin', 'school_admin', 'hr', 'teacher', 'storekeeper'] },
        { path: '/transport', label: 'Transport', icon: Truck, roles: ['super_admin', 'school_admin'], moduleKey: 'transport_module' },
        { path: '/hostel', label: 'Hostel', icon: Bed, roles: ['super_admin', 'school_admin'] },
        { path: '/front-office/visitors', label: 'Front Office', icon: DoorOpen, roles: ['super_admin', 'school_admin', 'registrar'] },
        { path: '/canteen-admin', label: 'Canteen QR', icon: QrCode, roles: ['super_admin', 'school_admin'] },
      ],
    },
    {
      label: 'Health', icon: FirstAidKit,
      roles: ['super_admin', 'school_admin', 'hr', 'nurse'],
      children: [
        { path: '/health/profiles', label: 'Health Profiles', icon: FirstAidKit, roles: ['super_admin', 'school_admin', 'hr', 'nurse'] },
        { path: '/health/visits', label: 'Clinic Visits', icon: FirstAidKit, roles: ['super_admin', 'school_admin', 'hr', 'nurse'] },
        { path: '/health/immunizations', label: 'Immunizations', icon: Syringe, roles: ['super_admin', 'school_admin', 'hr', 'nurse'] },
        { path: '/health/incidents', label: 'Health Incidents', icon: Ambulance, roles: ['super_admin', 'school_admin', 'hr', 'nurse'] },
        { path: '/health/screening-campaigns', label: 'Screening Campaigns', icon: MagnifyingGlass, roles: ['super_admin', 'school_admin', 'hr', 'nurse'] },
      ],
    },
    {
      label: 'Inventory', icon: Package,
      roles: ['super_admin', 'school_admin', 'hr', 'storekeeper'],
      children: [
        { path: '/inventory/assets', label: 'Assets', icon: Package, roles: ['super_admin', 'school_admin', 'hr', 'storekeeper'] },
        { path: '/inventory/stock-items', label: 'Stock Items', icon: Archive, roles: ['super_admin', 'school_admin', 'hr', 'storekeeper'] },
        { path: '/inventory/procurement', label: 'Procurement', icon: Package, roles: ['super_admin', 'school_admin', 'hr', 'storekeeper'] },
        { path: '/inventory/categories', label: 'Asset Categories', icon: Archive, roles: ['super_admin', 'school_admin', 'hr', 'storekeeper'] },
      ],
    },
    {
      label: 'Certificates & Credentials', icon: Certificate,
      roles: ['super_admin', 'school_admin', 'hr'],
      children: [
        { path: '/certificates/issuance', label: 'Certificates', icon: Certificate, roles: ['super_admin', 'school_admin'] },
        { path: '/certificates/templates', label: 'Certificate Templates', icon: Certificate, roles: ['super_admin', 'school_admin'] },
        { path: '/certificates/id-cards', label: 'ID Cards', icon: IdentificationCard, roles: ['super_admin', 'school_admin', 'hr'], moduleKey: 'id_cards' },
      ],
    },
    {
      label: 'Communication', icon: Megaphone,
      roles: ['super_admin', 'school_admin', 'teacher', 'student'],
      children: [
        { path: '/announcements', label: 'Announcements', icon: Megaphone, roles: ['super_admin', 'school_admin', 'teacher', 'student'] },
        { path: '/sms-dashboard', label: 'SMS Management', icon: Phone, roles: ['super_admin', 'school_admin'] },
        { path: '/tickets', label: 'Support Tickets', icon: Ticket, roles: ['super_admin', 'school_admin'] },
        { path: '/parent-requests-admin', label: 'Parent Requests', icon: FileText, roles: ['super_admin', 'school_admin', 'teacher', 'registrar', 'hr'] },
        { path: '/faq', label: 'FAQ', icon: Question, roles: ['super_admin', 'school_admin', 'hr', 'teacher', 'student', 'parent', 'registrar', 'security_officer', 'driver', 'nurse', 'storekeeper', 'canteen_staff', 'counselor', 'safeguarding_lead', 'sen_coordinator'] },
      ],
    },
    {
      label: 'Security', icon: ShieldCheck,
      roles: ['super_admin', 'school_admin'],
      children: adminSecurityItems,
    },
    {
      label: 'Programs', icon: ChalkboardTeacher,
      roles: ['super_admin', 'school_admin'],
      children: [
        { path: '/extra-classes-admin', label: 'Extra Classes', icon: ChalkboardTeacher, roles: ['super_admin', 'school_admin'] },
      ],
    },
    { path: '/reports', label: 'Reports', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr', 'registrar'] },
    { path: '/analytics', label: 'Analytics Dashboard', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr', 'teacher'] },
    { path: '/strategic-reports', label: 'Strategic Reports', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr'] },
    { path: '/executive-dashboard', label: 'Executive Dashboard', icon: ChartBar, roles: ['super_admin', 'school_admin'] },
    { path: '/executive-insights', label: 'Executive Insights', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr'] },
    { path: '/operational-insights', label: 'Operational Insights', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr'] },
    { path: '/strategic-planning', label: 'Strategic Planning', icon: ChartBar, roles: ['super_admin', 'school_admin', 'hr'] },
    {
      label: 'Administration', icon: Gear,
      roles: ['super_admin', 'school_admin'],
      children: [
        { path: '/campuses', label: 'Campuses', icon: MapTrifold, roles: ['super_admin', 'school_admin'] },
        { path: '/academic-calendar', label: 'Academic Calendar', icon: CalendarBlank, roles: ['super_admin', 'school_admin'], moduleKey: 'academic_reports' },
        { path: '/settings/roles', label: 'Roles & Permissions', icon: ShieldCheck, roles: ['super_admin', 'school_admin'] },
        { path: '/settings/integrations', label: 'API & Webhooks', icon: Plugs, roles: ['super_admin', 'school_admin'], moduleKey: 'integrations' },
        { path: '/settings', label: 'Settings', icon: Gear, roles: ['super_admin', 'school_admin'] },
      ],
    },
  ];

  // Navigation items for parents (simplified)
  const parentNavItems = [
    { path: '/parent-portal', label: 'My Children', icon: UserCircle, roles: ['parent'] },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['parent'] },
  ];

  // Navigation items for students (simplified)
  const studentNavItems = [
    { path: '/student-portal', label: 'My Portal', icon: UserCircle, roles: ['student'] },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['student'] },
  ];

  // Navigation items for students (simplified)
  const teacherNavItems = [
    { path: '/teacher-portal', label: 'My Workspace', icon: UserCircle, roles: ['teacher'] },
    { path: '/discipline/incidents', label: 'Discipline', icon: Warning, roles: ['teacher'] },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['teacher'] },
  ];

  // Navigation items for super admin (simplified)
  const superAdminNavItems = [
    { path: '/super-admin-portal', label: 'Admin Portal', icon: UserCircle, roles: ['super_admin'] },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['super_admin'] },
  ];

  // Navigation items for security officer
  const securityOfficerNavItems = [
    { path: '/security', label: 'Security Hub', icon: ShieldCheck, roles: ['security_officer'] },
    { path: '/security/verify', label: 'Scan QR', icon: QrCode, roles: ['security_officer'], moduleKey: 'pickup_advanced' },
    { path: '/security/admin-tracking', label: 'Live Tracker', icon: MapPin, roles: ['security_officer'] },
    { path: '/security/gate-attendance', label: 'Gate Attendance', icon: SignIn, roles: ['security_officer'], moduleKey: 'gate_attendance' },
    { path: '/front-office/visitors', label: 'Front Office', icon: DoorOpen, roles: ['security_officer'] },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['security_officer'] },
  ];

  // Navigation items for driver
  const driverNavItems = [
    { path: '/security/driver', label: 'My Route', icon: NavigationArrow, roles: ['driver'], moduleKey: 'transport_module' },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['driver'] },
  ];

  // "My Portal" home links prepended to the shared staff nav for roles that
  // have a dedicated portal landing page but still need staffNavItems'
  // scoped-by-role sections (Leave Requests, Health, Inventory, etc.)
  const hrPortalItem = { path: '/hr-portal', label: 'My Portal', icon: UserCircle, roles: ['hr'] };
  const nursePortalItem = { path: '/nurse-portal', label: 'My Portal', icon: UserCircle, roles: ['nurse'] };
  const registrarPortalItem = { path: '/registrar-portal', label: 'My Portal', icon: UserCircle, roles: ['registrar'] };
  const storekeeperPortalItem = { path: '/storekeeper-portal', label: 'My Portal', icon: UserCircle, roles: ['storekeeper'] };

  // Canteen staff have one operational page (order fulfillment + pickup
  // scanning inside CanteenAdminPage's Orders tab) rather than a separate
  // portal home — this is their whole sidebar.
  const canteenStaffNavItems = [
    { path: '/canteen-admin', label: 'My Portal', icon: QrCode, roles: ['canteen_staff'] },
    { path: '/faq', label: 'FAQ', icon: Question, roles: ['canteen_staff'] },
  ];

  // Choose nav items based on role
  let navItems;
  if (user?.role === 'parent') {
    navItems = parentNavItems;
  } else if (user?.role === 'student') {
    navItems = studentNavItems;
  } else if (user?.role === 'teacher') {
    navItems = teacherNavItems;
  } else if (user?.role === 'super_admin') {
    navItems = [...superAdminNavItems, ...adminSecurityItems];
  } else if (user?.role === 'security_officer') {
    navItems = securityOfficerNavItems;
  } else if (user?.role === 'driver') {
    navItems = driverNavItems;
  } else if (user?.role === 'hr') {
    navItems = [hrPortalItem, ...staffNavItems];
  } else if (user?.role === 'nurse') {
    navItems = [nursePortalItem, ...staffNavItems];
  } else if (user?.role === 'registrar') {
    navItems = [registrarPortalItem, ...staffNavItems];
  } else if (user?.role === 'storekeeper') {
    navItems = [storekeeperPortalItem, ...staffNavItems];
  } else if (user?.role === 'canteen_staff') {
    navItems = canteenStaffNavItems;
  } else {
    navItems = staffNavItems;
  }

  // Leaf items are kept if the role matches; groups are kept (with only
  // their matching children) if at least one child matches the role.
  const filteredNavItems = navItems.reduce((acc, item) => {
    if (item.children) {
      const visibleChildren = item.children.filter(child => child.roles.includes(user?.role));
      if (visibleChildren.length > 0) {
        acc.push({ ...item, children: visibleChildren });
      }
    } else if (item.roles.includes(user?.role)) {
      acc.push(item);
    }
    return acc;
  }, []);

  const [openGroups, setOpenGroups] = useState(() => new Set());

  // Auto-expand whichever group contains the currently active route.
  useEffect(() => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      navItems.forEach(item => {
        if (item.children && item.children.some(child => child.path === location.pathname)) {
          next.add(item.label);
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (label) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <svg viewBox="0 0 40 40" className="logo-icon-small">
              <rect x="4" y="8" width="32" height="24" rx="2" fill="currentColor" opacity="0.2"/>
              <rect x="8" y="12" width="24" height="16" rx="1" fill="currentColor"/>
              <path d="M20 18l8-4v12l-8 4-8-4V14l8 4z" fill="white"/>
            </svg>
            <span>Campusio</span>
          </div>
          <button className="sidebar-close" onClick={onClose}>×</button>
        </div>
        
        <nav className="sidebar-nav">
          {filteredNavItems.map(item => {
            if (item.children) {
              const GroupIcon = item.icon;
              const isOpen = openGroups.has(item.label);
              const isGroupActive = item.children.some(child => child.path === location.pathname);
              return (
                <Collapsible key={item.label} open={isOpen} onOpenChange={() => toggleGroup(item.label)}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className={`nav-item nav-group-trigger ${isGroupActive ? 'active' : ''}`}
                      data-testid={`nav-group-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <span className="nav-icon"><GroupIcon size={20} weight="duotone" /></span>
                      <span className="nav-label">{translateNavLabel(item.label)}</span>
                      <CaretDown size={16} className={`nav-group-caret ${isOpen ? 'nav-group-caret--open' : ''}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="nav-group-content">
                    {item.children.map(child => {
                      const ChildIcon = child.icon;
                      const childLocked = child.moduleKey && !hasModule(child.moduleKey);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`nav-item nav-item--child ${location.pathname === child.path ? 'active' : ''} ${childLocked ? 'nav-item--locked' : ''}`}
                          onClick={(e) => {
                            if (childLocked) {
                              e.preventDefault();
                              showUpgradeRequired(`${child.label} requires a higher subscription plan. Contact your school administrator to upgrade.`);
                              return;
                            }
                            onClose();
                          }}
                          data-testid={`nav-${child.path.slice(1)}`}
                        >
                          <span className="nav-icon"><ChildIcon size={18} weight="duotone" /></span>
                          <span className="nav-label">{child.label}</span>
                          {childLocked && <Lock size={14} weight="fill" className="nav-lock-icon" />}
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            }

            const Icon = item.icon;
            const locked = item.moduleKey && !hasModule(item.moduleKey);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''} ${locked ? 'nav-item--locked' : ''}`}
                onClick={(e) => {
                  if (locked) {
                    e.preventDefault();
                    showUpgradeRequired(`${item.label} requires a higher subscription plan. Contact your school administrator to upgrade.`);
                    return;
                  }
                  onClose();
                }}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <span className="nav-icon"><Icon size={20} weight="duotone" /></span>
                <span className="nav-label">{translateNavLabel(item.label)}</span>
                {locked && <Lock size={14} weight="fill" className="nav-lock-icon" />}
              </Link>
            );
          })}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.first_name} {user?.last_name}</span>
              <span className="user-role">{user?.role?.replace('_', ' ')}</span>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout} data-testid="logout-button">
            <SignOut size={16} weight="bold" /> {t('nav.signOut')}
          </button>
          <LanguageSwitcher className="sidebar-language-switcher" />
        </div>
      </aside>
    </>
  );
}

// Main Layout — passthrough when already mounted via AppShell (avoids sidebar remount on navigation)
function Layout({ children }) {
  const alreadyMounted = useContext(LayoutMountedContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  if (alreadyMounted) {
    // Already inside AppShell — just render content, no extra sidebar
    return <>{children}</>;
  }

  return (
    <LayoutMountedContext.Provider value={true}>
      <div className="layout">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <div className="main-content">
          <header className="header">
            <button className="menu-toggle" onClick={() => setSidebarOpen(true)} data-testid="menu-toggle">
              <List size={24} weight="bold" />
            </button>
            <h2 className="page-title">Campusio</h2>
            <div className="header-user">
              <span>{user?.first_name}</span>
            </div>
          </header>

          <main className="content">
            {children}
          </main>
        </div>
      </div>
    </LayoutMountedContext.Provider>
  );
}

// AppShell — used in App.js as a persistent route wrapper so the sidebar
// never unmounts when navigating between pages.
function AppShell() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

export { Sidebar, Layout, AppShell };
export default Layout;
